import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { useAuth } from '../lib/auth'
import { StatusBadge } from '../components/Status'
import { CopyField } from '../components/CopyField'

/** Eigenes Konto: Kennwort ändern und Zwei-Faktor-Anmeldung einrichten. */
export function KontoPage() {
  const { me, refresh } = useAuth()
  if (!me) return null

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Eigenes Konto</h1>
          <div className="page-sub">
            {me.name} · {me.email}
          </div>
        </div>
      </div>

      <PasswordSection />
      <MFASection enabled={me.mfa_enabled} onChanged={refresh} />
      {me.mfa_enabled ? <TelefonSection /> : null}
    </>
  )
}

/**
 * Mobilnummer für SMS und Anruf aus dem Bereitschaftsplan (Stufe 2.4).
 * Sie steht nur hier; wer Bereitschaft hat, bekommt Alarme dorthin.
 */
function TelefonSection() {
  const gespeichert = useQuery({
    queryKey: ['me-telefon'],
    queryFn: ({ signal }) => apiGet<{ telefon: string }>('/me/telefon', signal),
  })
  const [telefon, setTelefon] = useState('')
  useEffect(() => {
    if (gespeichert.data) setTelefon(gespeichert.data.telefon)
  }, [gespeichert.data])
  const speichern = useMutation({
    mutationFn: () => apiSend<void>('PUT', '/me/telefon', { telefon: telefon.trim() }),
    onSuccess: () => void gespeichert.refetch(),
  })
  const unveraendert = (gespeichert.data?.telefon ?? '') === telefon.replace(/\s/g, '')

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Mobilnummer</h2>
      </div>
      <form
        className="card-body row"
        style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}
        onSubmit={(e) => {
          e.preventDefault()
          speichern.mutate()
        }}
      >
        <div className="field" style={{ minWidth: 240 }}>
          <label htmlFor="konto-telefon">Mobilnummer (international, +43…)</label>
          <input
            id="konto-telefon"
            className="input mono"
            type="tel"
            autoComplete="tel"
            value={telefon}
            placeholder="+43 664 1234567"
            onChange={(e) => setTelefon(e.target.value)}
          />
        </div>
        <button type="submit" className="btn" disabled={speichern.isPending || unveraendert}>
          Mobilnummer speichern
        </button>
        <span className="muted" style={{ fontSize: 12, flex: 1, minWidth: 220 }}>
          Wer Bereitschaft hat, bekommt SMS und Anrufe an diese Nummer. Leer lassen, um sie zu entfernen.
        </span>
        {speichern.isSuccess && unveraendert ? <span style={{ color: 'var(--ok)' }}>Gespeichert.</span> : null}
        {speichern.isError ? <span style={{ color: 'var(--crit)' }}>{(speichern.error as Error).message}</span> : null}
      </form>
    </section>
  )
}

function PasswordSection() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')

  const change = useMutation({
    mutationFn: () =>
      apiSend<void>('POST', '/me/password', {
        current_password: current,
        new_password: next,
      }),
    onSuccess: () => {
      setCurrent('')
      setNext('')
      setRepeat('')
    },
  })

  const mismatch = repeat !== '' && repeat !== next

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Kennwort ändern</h2>
      </div>
      <form
        className="card-body stack"
        style={{ maxWidth: 420 }}
        onSubmit={(e) => {
          e.preventDefault()
          if (current && next.length >= 12 && !mismatch) change.mutate()
        }}
      >
        <p className="muted" style={{ margin: 0 }}>
          Nach dem Wechsel enden alle anderen angemeldeten Sitzungen.
        </p>
        <div className="field">
          <label htmlFor="pw-current">Bisheriges Kennwort</label>
          <input
            id="pw-current"
            className="input"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="pw-next">Neues Kennwort (mindestens 12 Zeichen)</label>
          <input
            id="pw-next"
            className="input"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="pw-repeat">Wiederholen</label>
          <input
            id="pw-repeat"
            className="input"
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
          {mismatch ? (
            <span style={{ color: 'var(--crit)', fontSize: 13 }}>
              Die beiden Eingaben stimmen nicht überein.
            </span>
          ) : null}
        </div>
        {change.isError ? (
          <div role="alert" style={{ color: 'var(--crit)' }}>
            {(change.error as Error).message}
          </div>
        ) : null}
        {change.isSuccess ? <div style={{ color: 'var(--ok)' }}>Kennwort geändert.</div> : null}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={change.isPending || !current || next.length < 12 || mismatch}
        >
          Kennwort ändern
        </button>
      </form>
    </section>
  )
}

/**
 * Einrichtung der Zwei-Faktor-Anmeldung. Wird auf der Kontoseite gezeigt und,
 * solange sie noch fehlt, als eigene Seite vor der Anwendung (MfaEinrichtung).
 */
export function MFASection({ enabled, onChanged }: { enabled: boolean; onChanged: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null)
  const [code, setCode] = useState('')

  const begin = useMutation({
    mutationFn: () => apiSend<{ secret: string; uri: string }>('POST', '/me/mfa/begin'),
    onSuccess: (data) => setSetup(data),
  })
  const confirm = useMutation({
    mutationFn: () => apiSend<void>('POST', '/me/mfa/confirm', { code: code.trim() }),
    onSuccess: () => {
      setSetup(null)
      setCode('')
      onChanged()
    },
  })

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Zwei-Faktor-Anmeldung</h2>
        <StatusBadge state={enabled ? 'ok' : 'warn'} label={enabled ? 'Eingerichtet' : 'Nicht eingerichtet'} />
      </div>
      <div className="card-body stack" style={{ maxWidth: 560 }}>
        {enabled && !setup ? (
          <p className="muted" style={{ margin: 0 }}>
            Bei jeder Anmeldung wird zusätzlich ein Code aus der Authenticator-App verlangt. Um ein
            neues Gerät einzurichten, den Vorgang unten erneut starten.
          </p>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            Eine Authenticator-App erzeugt alle 30 Sekunden einen sechsstelligen Code. Ohne diesen
            Code reicht ein gestohlenes Kennwort nicht aus.
          </p>
        )}

        {setup ? (
          <>
            <CopyField label="Geheimnis für die App (manuelle Eingabe)" value={setup.secret} />
            <CopyField label="otpauth-Adresse (QR-Code im Authenticator)" value={setup.uri} />
            <div className="field" style={{ maxWidth: 220 }}>
              <label htmlFor="mfa-code">Code aus der App</label>
              <input
                id="mfa-code"
                className="input mono"
                inputMode="numeric"
                maxLength={7}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            {confirm.isError ? (
              <div role="alert" style={{ color: 'var(--crit)' }}>
                {(confirm.error as Error).message}
              </div>
            ) : null}
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={confirm.isPending || code.trim().length < 6}
                onClick={() => confirm.mutate()}
              >
                Einrichtung abschließen
              </button>
              <button type="button" className="btn" onClick={() => setSetup(null)}>
                Abbrechen
              </button>
            </div>
          </>
        ) : (
          <div>
            <button type="button" className="btn btn-primary" disabled={begin.isPending} onClick={() => begin.mutate()}>
              {enabled ? 'Neu einrichten' : 'Einrichten'}
            </button>
            {begin.isError ? (
              <div role="alert" style={{ color: 'var(--crit)', marginTop: 8 }}>
                {(begin.error as Error).message}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  )
}
